import React from 'react';
import { Badge, Flex, Text, Tooltip } from '@radix-ui/themes';
import {
  ListBulletIcon,
  BellIcon,
  GearIcon,
  PlusIcon,
} from '@radix-ui/react-icons';

type BadgeColor =
  | 'gray'
  | 'gold'
  | 'bronze'
  | 'brown'
  | 'yellow'
  | 'amber'
  | 'orange'
  | 'tomato'
  | 'red'
  | 'ruby'
  | 'crimson'
  | 'pink'
  | 'plum'
  | 'purple'
  | 'violet'
  | 'iris'
  | 'indigo'
  | 'blue'
  | 'cyan'
  | 'teal'
  | 'jade'
  | 'green'
  | 'grass'
  | 'lime'
  | 'mint'
  | 'sky';

export type ChipType = 'list' | 'alert' | 'workflow';
export type ChipVariant = 'count' | 'add';

const chipConfig: Record<
  ChipType,
  {
    icon: React.ReactNode;
    label: string;
    addLabel: string;
    color: BadgeColor;
    emptyColor: BadgeColor;
  }
> = {
  list: {
    icon: <ListBulletIcon width={12} height={12} />,
    label: 'Lists',
    addLabel: '+ List',
    color: 'blue',
    emptyColor: 'gray',
  },
  alert: {
    icon: <BellIcon width={12} height={12} />,
    label: 'Alerts',
    addLabel: '+ Alert',
    color: 'amber',
    emptyColor: 'gray',
  },
  workflow: {
    icon: <GearIcon width={12} height={12} />,
    label: 'Workflows',
    addLabel: '+ Workflow',
    color: 'violet',
    emptyColor: 'gray',
  },
};

export interface InstrumentationChipProps {
  /** The type of instrumentation this chip represents */
  type: ChipType;
  /** The count of items (for count variant) */
  count?: number;
  /** The variant: 'count' shows existing items, 'add' shows creation action */
  variant?: ChipVariant;
  /** Click handler - opens contextual panel */
  onClick?: () => void;
  /** Whether the chip is disabled */
  disabled?: boolean;
  /** Optional tooltip text override */
  tooltip?: string;
}

const InstrumentationChip: React.FC<InstrumentationChipProps> = ({
  type,
  count = 0,
  variant = 'count',
  onClick,
  disabled = false,
  tooltip,
}) => {
  const config = chipConfig[type];
  const isEmpty = variant === 'count' && count === 0;
  const isAdd = variant === 'add';

  const color = isEmpty || isAdd ? config.emptyColor : config.color;

  const content = isAdd ? (
    <Flex align="center" gap="1">
      <PlusIcon width={10} height={10} />
      <Text size="1">{config.label.slice(0, -1)}</Text>
    </Flex>
  ) : (
    <Flex align="center" gap="1">
      {config.icon}
      <Text size="1">{count}</Text>
      <Text size="1">{config.label}</Text>
    </Flex>
  );

  const defaultTooltip = isAdd
    ? `Add to ${config.label.toLowerCase()}`
    : isEmpty
      ? `No ${config.label.toLowerCase()} configured`
      : `View ${count} ${count === 1 ? config.label.slice(0, -1).toLowerCase() : config.label.toLowerCase()}`;

  const badge = (
    <Badge
      radius="full"
      color={color}
      variant={isEmpty || isAdd ? 'soft' : 'solid'}
      size="1"
      style={{
        cursor: disabled ? 'not-allowed' : onClick ? 'pointer' : 'default',
        opacity: disabled ? 0.5 : isEmpty ? 0.7 : 1,
        transition: 'opacity 0.15s ease, transform 0.1s ease',
      }}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={(e) => {
        if (!disabled && onClick) {
          e.currentTarget.style.transform = 'scale(1.02)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
      }}
    >
      {content}
    </Badge>
  );

  return <Tooltip content={tooltip || defaultTooltip}>{badge}</Tooltip>;
};

export default InstrumentationChip;
